import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useBranding } from "../lib/branding";
import { HardHat, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const branding = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [canRegister, setCanRegister] = useState(false);
  const [bootstrapAvailable, setBootstrapAvailable] = useState(false);

  useEffect(() => {
    api.get("/auth/registration-status")
      .then((r) => { setCanRegister(!!r.data?.can_register); setBootstrapAvailable(!!r.data?.bootstrap_available); })
      .catch(() => { setCanRegister(false); setBootstrapAvailable(false); });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenue !");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left visual */}
      <div className="hidden lg:flex blueprint-bg flex-col justify-between p-12 text-white">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-btp-terra flex items-center justify-center shadow-lg overflow-hidden">
            {branding.company_logo_url ? (
              <img src={branding.company_logo_url} alt="logo" className="w-full h-full object-cover" />
            ) : (
              <HardHat size={22} />
            )}
          </div>
          <div>
            <div className="font-heading font-bold text-xl tracking-tight" data-testid="brand-name-hero">{branding.company_name}</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400">{branding.company_tagline}</div>
          </div>
        </div>

        <div className="max-w-lg">
          <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra mb-4 font-semibold">Plateforme intégrée</div>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            La gestion de projets BTP<br/>repensée pour l'Afrique.
          </h1>
          <p className="mt-6 text-slate-300 text-base leading-relaxed">
            ERP + GED + Planning + Budget. De la réception du TDR à la livraison finale.
            Conçu pour bureaux d'études en génie civil opérant sur terrain.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6">
            <div>
              <div className="font-heading text-3xl font-bold">187k</div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">km étudiés</div>
            </div>
            <div>
              <div className="font-heading text-3xl font-bold">12</div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">pays Afrique</div>
            </div>
            <div>
              <div className="font-heading text-3xl font-bold">98%</div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">livrables à l'heure</div>
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-500 font-mono">© 2026 {branding.company_name} — APS / APD / DAO</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-md bg-btp-terra flex items-center justify-center overflow-hidden">
              {branding.company_logo_url ? (
                <img src={branding.company_logo_url} alt="logo" className="w-full h-full object-cover" />
              ) : (
                <HardHat size={20} className="text-white" />
              )}
            </div>
            <div className="font-heading font-bold text-xl" data-testid="brand-name-mobile">{branding.company_name}</div>
          </div>

          <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra mb-3 font-semibold">Connexion</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Accéder au cockpit</h2>
          <p className="text-slate-500 text-sm mt-2">Identifiez-vous pour piloter vos projets et missions.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@bureau-etudes.com"
                className="mt-1.5 w-full h-11 px-3 border border-slate-300 rounded-md text-sm focus:border-btp-navy focus:outline-none focus:ring-1 focus:ring-btp-navy"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Mot de passe</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 w-full h-11 px-3 border border-slate-300 rounded-md text-sm focus:border-btp-navy focus:outline-none focus:ring-1 focus:ring-btp-navy"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-60 text-white rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Se connecter <ArrowRight size={16} /></>}
            </button>
          </form>



          <div className="mt-6 text-center text-sm text-slate-500">
            {bootstrapAvailable ? (
              <>Premier démarrage ? <Link to="/register" className="text-btp-navy font-semibold hover:underline" data-testid="link-register">Créer le compte super administrateur</Link></>
            ) : canRegister ? (
              <>Pas de compte ? <Link to="/register" className="text-btp-navy font-semibold hover:underline" data-testid="link-register">Créer un compte</Link></>
            ) : (
              <span className="text-xs text-slate-400">Les comptes sont créés par votre super administrateur.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
